import React, { useState } from "react";
import { Plus } from "lucide-react";
import {
  DndContext,
  closestCenter,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import TaskItem from "../../components/TaskItem";
import phaseTaskService from "../../services/phaseTaskService";

/* ===================================================== */
/* 🔧 NORMALIZER (CRITICAL)                              */
/* ===================================================== */

const normalizeTask = (task, fallbackPhaseId = null) => {
  if (!task || typeof task !== "object") return null;

  const t = task._id ? task : task.task;

  if (!t || !t._id) return null;

  return {
    ...t,
    _id: String(t._id),
    phaseId:
      typeof t.phaseId === "string"
        ? t.phaseId
        : t.phaseId?._id
        ? String(t.phaseId._id)
        : fallbackPhaseId
        ? String(fallbackPhaseId)
        : null,
  };
};

/**
 * PhaseTasksGroup
 */
export default function PhaseTasksGroup({
  phase,
  projectId,
  tasks = [],
  users = [],
  allPhases = [],
  onTasksUpdate,
}) {
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");

  /* ===================================================== */
  /* 🎯 DROPPABLE                                          */
  /* ===================================================== */

  const { setNodeRef } = useDroppable({
    id: `phase-${phase._id}`,
  });

  /* ===================================================== */
  /* ➕ CREATE TASK                                        */
  /* ===================================================== */

  const handleCreate = async () => {
    const title = newTitle.trim();

    if (!title) {
      setErr("Task title is required");
      return;
    }

    try {
      setCreating(true);
      setErr("");

      const createdRaw =
        await phaseTaskService.createPhaseTask(
          projectId,
          phase._id,
          { title }
        );

      const created = normalizeTask(createdRaw, phase._id);

      if (!created) {
        throw new Error("Invalid task response");
      }

      onTasksUpdate?.((prev) => [created, ...prev]);

      setNewTitle("");

    } catch (e) {
      setErr(
        e?.response?.data?.message ||
        e.message ||
        "Failed to create task"
      );
    } finally {
      setCreating(false);
    }
  };

  /* ===================================================== */
  /* 🔀 DRAG BETWEEN PHASES                                */
  /* ===================================================== */

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) return;

    const taskId = active.id;
    const overId = over.id;

    if (!overId.startsWith("phase-")) return;

    const targetPhaseId = overId.replace("phase-", "");

    const current = tasks.find((t) => t._id === taskId);
    if (!current) return;

    if (current.phaseId === targetPhaseId) return;

    try {
      const updatedRaw =
        await phaseTaskService.updatePhaseTask(taskId, {
          phaseId: targetPhaseId,
        });

      const updated = normalizeTask(
        updatedRaw,
        targetPhaseId
      );

      if (!updated) {
        throw new Error("Invalid update response");
      }

      onTasksUpdate?.((prev) =>
        prev.map((t) =>
          t._id === updated._id ? updated : t
        )
      );

    } catch (e) {
      console.error("Drag error:", e);
      alert("Failed to move task");
    }
  };

  /* ===================================================== */
  /* 🧩 RENDER                                             */
  /* ===================================================== */

  return (
    <div ref={setNodeRef} className="rounded-xl border bg-white">

      {/* HEADER */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <div>
          <h3 className="font-semibold text-gray-900">
            {phase.name}
          </h3>
          {phase.description && (
            <p className="text-xs text-gray-500 mt-0.5">
              {phase.description}
            </p>
          )}
        </div>

        <span className="text-xs text-gray-500">
          {tasks.length} task{tasks.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* TASK LIST */}
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tasks.map((t) => t._id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.length === 0 ? (
            <div className="px-4 py-4 text-sm text-gray-500 italic">
              Drop tasks here or add a new one.
            </div>
          ) : (
            <ul className="divide-y">
              {tasks.map((task) => (
                <TaskItem
                  key={task._id}
                  task={task}
                  users={users}
                  phases={allPhases}

onToggle={async (task) => {
  try {
    const nextStatus =
      task.status === "completed"
        ? "pending"
        : "completed";

    const updated = await phaseTaskService.updatePhaseTask(
      task._id,
      { status: nextStatus }
    );

    onTasksUpdate?.((prev) =>
      prev.map((t) =>
        t._id === updated._id ? updated : t
      )
    );
  } catch (err) {
  console.error("Toggle failed:", err);

  alert(
    err?.message ||
    "Failed to update task status"
  );
}
}}
                  onDelete={() =>
                    onTasksUpdate?.((prev) =>
                      prev.filter((t) => t._id !== task._id)
                    )
                  }

                  /* 🔥 FINAL FIX HERE */
                  onLocalUpdate={(updatedRaw) => {
                    const updated = normalizeTask(
                      updatedRaw,
                      task.phaseId
                    );

                    if (!updated) return;

                    onTasksUpdate?.((prev) =>
                      prev.map((t) =>
                        t._id === updated._id
                          ? updated
                          : t
                      )
                    );
                  }}
                />
              ))}
            </ul>
          )}
        </SortableContext>
      </DndContext>

      {/* QUICK ADD */}
      <div className="flex items-center gap-2 px-4 py-3 border-t bg-gray-50">
        <input
          value={newTitle}
          onChange={(e) => {
            setNewTitle(e.target.value);
            if (err) setErr("");
          }}
          onKeyDown={(e) =>
            e.key === "Enter" && handleCreate()
          }
          placeholder={`Add task to "${phase.name}"`}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring focus:ring-gray-200"
        />

        <button
          onClick={handleCreate}
          disabled={creating}
          className="inline-flex items-center gap-1 rounded-lg bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {creating ? "Adding..." : "Add"}
        </button>
      </div>

      {err && (
        <div className="px-4 pb-3 text-sm text-rose-600">
          {err}
        </div>
      )}
    </div>
  );
}